import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { DemandChecklistItemDto, GetDemandChecklistResponseDto } from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { demandsQueryKeys } from '@/features/idea-management/queryKeys/demands';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

/**
 * The guided submission-to-ready checklist (NEX-16801). Read-only apart from evidence: every other
 * step is flipped server-side by whichever routine owns its data, so there is nothing for a client
 * to write. That is also why the mutations here take no body - the step and its value are both
 * implied by the route.
 */
export const useDemandChecklistApi = (httpClient: AxiosInstance) => {
  const getChecklist = async (demandId: number): Promise<DemandChecklistItemDto[]> => {
    const { data } = await httpClient.get<GetDemandChecklistResponseDto>(
      `/demands/${demandId}/checklist`,
    );
    return data.data;
  };

  const setEvidenceSufficiency = async ({
    demandId,
    isSufficient,
  }: {
    demandId: number;
    isSufficient: boolean;
  }): Promise<DemandChecklistItemDto> => {
    const action = isSufficient ? 'mark-sufficient' : 'mark-insufficient';
    const { data } = await httpClient.put<{ data: DemandChecklistItemDto }>(
      `/demands/${demandId}/checklist/evidence/${action}`,
    );
    return data.data;
  };

  const queryClient = useQueryClient();

  const useGetChecklist = (demandId: number) =>
    useQuery({
      queryKey: demandsQueryKeys.checklist(demandId),
      queryFn: () => getChecklist(demandId),
      enabled: demandId > 0,
    });

  /**
   * Marking evidence writes an activity entry as well as the step, so the Conversation feed is
   * invalidated alongside the checklist.
   */
  const useSetEvidenceSufficiency = () =>
    useErrorHandlingMutation({
      mutationFn: setEvidenceSufficiency,
      onSuccess: (data, variables) => {
        const checklistQueryKey = demandsQueryKeys.checklist(variables.demandId);
        queryClient.setQueryData<DemandChecklistItemDto[]>(
          checklistQueryKey,
          (previous) =>
            previous?.map((item) => (item.step === data.step ? data : item)) ?? previous,
        );
        return Promise.all([
          // The endpoint returns one item, not the canonical five-step list. Refetch instead of
          // creating a one-item checklist when the query has not loaded (or previously failed).
          queryClient.invalidateQueries({ queryKey: checklistQueryKey }),
          queryClient.invalidateQueries({
            queryKey: demandsQueryKeys.activity(variables.demandId),
          }),
        ]);
      },
    });

  return { getChecklist, useGetChecklist, useSetEvidenceSufficiency };
};
